import { useLaunchDarkly } from "~/composables/useLaunchDarkly";
import routes from "~/constants/routes";
import { useNavigationStore } from "@/store/navigation";
import { checkNavigationAccess } from "~/utils/FeatureNavigationCheck";
import type { sidebarOptions } from "~/types/NavigationStateType";
import { useUpNavigationStore } from "~/store/upNavigationStore.ts";
import { useLoadingStore } from "@/store/loadingStore";

export default defineNuxtRouteMiddleware(async (to) => {
  if (import.meta.server) return;

  const loadingStore = useLoadingStore();
  const taskId = `navigation-middleware-${Date.now()}`;
  loadingStore.startTask(taskId);

  const navigationStore = useNavigationStore();
  const upNavigationStore = useUpNavigationStore();
  const ld = await useLaunchDarkly();
  const { isUpNavFlagReady, upNavFlag } = storeToRefs(upNavigationStore);
  const { fetchUpNavFlag } = upNavigationStore;

  if (!isUpNavFlagReady.value) {
    await fetchUpNavFlag();
  }

  if (upNavFlag.value) {
    loadingStore.endTask(taskId);
    return;
  }

  let showNewPPTReportsFlag = false;
  if (!ld.isLDClient()) {
    showNewPPTReportsFlag = await ld.getFlagValue("showNewPPTReports");
  }

  let path = to.fullPath.includes("?")
    ? to.fullPath.substring(0, to.fullPath.indexOf("?"))
    : to.fullPath;

  // Handle edge cases
  if (to.fullPath.includes("graph=articles")) {
    path = `${path}?graph=articles`;
  } else if (to.fullPath.includes("graph=topics")) {
    path = `${path}?graph=topics`;
  } else if (to.fullPath.includes("/ppt-reports") && !showNewPPTReportsFlag) {
    // TODO: Remove this check once we fully migrate to the new PPT Reports
    loadingStore.endTask(taskId);
    return navigateTo("/search");
  }

  routes.every((route) => {
    return route.subRoutes.every((subRoute) => {
      if (subRoute?.path !== "/" && path === subRoute?.path) {
        navigationStore.setActiveSideBarOption(route.name as sidebarOptions);
        navigationStore.setActiveNavBarOption(subRoute.id);
        navigationStore.setActiveNavBarOptions(route.subRoutes);
        return false;
      }
      return true;
    });
  });

  // This block handles the edge case where we hit the non reports pages
  if (!path.includes("/reports")) {
    document.getElementById("cm-reports-app")?.remove();
  }

  // This block handles the edge case where we hit the mention & publication tabs
  if (
    path.includes("manage/added-mentions") ||
    path.includes("manage/excluded-mentions") ||
    path.includes("manage/requested-publications") ||
    path.includes("manage/excluded-publication") ||
    path.includes("/purchased-content")
  ) {
    navigationStore.setActiveSideBarOption(routes[3].name as sidebarOptions);
    navigationStore.setActiveNavBarOption(routes[3].subRoutes[2].id);
    navigationStore.setActiveNavBarOptions(routes[3].subRoutes);
  }

  // This block handles the edge case where we hit the alerts/digest sub tabs
  if (path.includes("alerts/real-time") || path.includes("alerts/digest")) {
    navigationStore.setActiveSideBarOption(routes[3].name as sidebarOptions);
    navigationStore.setActiveNavBarOption(routes[3].subRoutes[1].id);
    navigationStore.setActiveNavBarOptions(routes[3].subRoutes);
  }

  // This block handles the edge case where we hit the search edit page
  // from the monitor tab of the sidebar
  if (path.includes("searches/edit")) {
    navigationStore.setActiveSideBarOption(routes[2].name as sidebarOptions);
    navigationStore.setActiveNavBarOption(routes[2].subRoutes[4].id);
    navigationStore.setActiveNavBarOptions(routes[2].subRoutes);
  }

  // This block handles the edge case where settings page doesn't display sidebars
  if (path.includes("settings/")) {
    navigationStore.setActiveSideBarOption(routes[4].name as sidebarOptions);
    navigationStore.setActiveNavBarOption(routes[4].subRoutes[0].id);
    navigationStore.setActiveNavBarOptions(routes[4].subRoutes);
  }

  const activeRoute = navigationStore.activeNavBarOptions;
  activeRoute?.forEach((subRoute) => {
    // Enable nav bar options based on FMS features
    const hasFeatureAccess = checkNavigationAccess(subRoute.translationKey);

    if (hasFeatureAccess) {
      navigationStore.setDisplay(subRoute.id, true);
    }
  });

  loadingStore.endTask(taskId);
});
